import {
  billingEngineQueries,
  RestAPI,
  routes,
  useCoordinator,
  useCurrentUser,
  useRouter,
} from '@builddocs/data-management'
import { useOrganization } from '@builddocs/data-management'
import {
  Avatar,
  Box,
  Divider,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks'
import { useQuery } from 'react-query'
import { Link as LinkRouter } from 'react-router-dom'

import { UserOption } from './atoms/UserOption'

export const ProfileField = () => {
  const theme = useTheme()
  const { user } = useCurrentUser()
  const { navigate } = useRouter()
  const { projectId } = useCoordinator()
  const logout = () => {
    navigate(routes.auth.logout())
  }
  const { data: projectSettings } = useQuery(
    billingEngineQueries.getProjectSettings({ projectId }),
  )
  const handleTariffsClick = () => {
    navigate(`${routes.tariffs.root()}?list`)
  }

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'popover-notifications',
  })

  const bindPopoverProps = bindPopover(popupState)

  const { data: linkedAccounts } = useQuery({
    queryKey: ['linked-accounts', user?.userId],
    queryFn: () => RestAPI.get('/users/accounts').then(res => res.data),
    enabled: !!user?.userId,
  })

  const { organization, isLoading } = useOrganization()

  const secondaryText = isLoading ? '' : organization?.name ?? 'Без организации'

  return (
    <Stack alignItems="flex-end" data-testid="profile-field" id="profile-field">
      <Tooltip title="Меню">
        <ListItem disablePadding sx={{ width: 'fit-content', display: 'flex' }}>
          <ListItemButton
            data-testid="profile-field-button"
            selected={popupState.isOpen}
            sx={{
              gap: 1,
              borderRadius: { xs: '50%', md: 2 },
              py: { xs: 0.5, md: 0.5 },
              px: 0,
            }}
            {...bindTrigger(popupState)}
          >
            <ListItemText
              primary={[user?.firstName, user?.lastName].join(' ')}
              primaryTypographyProps={{
                lineHeight: 1.35,
                color: 'text.primary',
              }}
              secondary={secondaryText}
              secondaryTypographyProps={{ lineHeight: 1.35 }}
              sx={{
                textAlign: 'right',
                margin: 0,
                display: { xs: 'none', md: 'initial' },
                justifyContent: 'center',
                whiteSpace: 'nowrap',
                height: '100%',
              }}
            />
            <ListItemAvatar sx={{ minWidth: 28 }}>
              <Avatar
                src=""
                sx={{
                  backgroundColor: 'transparent',
                  height: 32,
                  width: 32,
                  borderWidth: 2,
                  borderStyle: 'solid',
                  borderColor: 'transparent',
                  svg: {
                    backgroundColor: 'grey.300',
                    color: 'grey.900',
                    width: '100%',
                    height: '100%',
                    p: 0.5,
                  },
                  transition: theme.transitions.create([
                    'border-color',
                    'transform',
                  ]),
                  ...(bindPopoverProps.open && {
                    borderColor: 'primary.main',
                  }),
                }}
                variant="circular"
              />
            </ListItemAvatar>
          </ListItemButton>
        </ListItem>
      </Tooltip>
      <Menu
        {...bindPopoverProps}
        MenuListProps={{ sx: { minWidth: 128 } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        data-testid="profile-field-menu"
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          component={LinkRouter}
          sx={{
            '& a': {
              color: 'text.primary',
              textDecoration: 'none',
              width: '100%',
            },
          }}
          to={routes.account.base()}
          onClick={() => popupState.setOpen(false)}
        >
          <Typography>Профиль</Typography>
        </MenuItem>
        {!!projectSettings?.shouldShowTariffMenu && (
          <MenuItem onClick={handleTariffsClick}>
            <Typography>Тарифы</Typography>
          </MenuItem>
        )}
        <MenuItem onClick={logout}>
          <Typography>Выйти</Typography>
        </MenuItem>
        {linkedAccounts?.length > 1 && (
          <>
            <Divider sx={{ my: '4px' }} />
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px',
                letterSpacing: '0px',
                color: '#777777',
                margin: '6px 8px 2px 8px',
              }}
            >
              Связанные учетные записи
            </Typography>
            <Box sx={{ px: 1, pb: 1 }}>
              {linkedAccounts?.map(account => (
                <UserOption
                  key={account.userId}
                  account={account}
                  isActive={account.userId === user?.userId}
                />
              ))}
            </Box>
          </>
        )}
      </Menu>
    </Stack>
  )
}

export default ProfileField
